import axios from 'axios';
import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import isEmpty from 'lodash/isEmpty';
import { JSONParse } from 'json-with-bigint';
import dompurify from 'dompurify';

import HttpStatusCode from './HttpStatusCode.enum';
import Util from './Util';
import Analytics from './Analytics';
import Session from './Session';
import { DateRange } from '../data/DateRangeOptions';

export const CommunicationStatus = Object.freeze({
	Success: 'Success',
	Failure: 'Failure',
	SignedOut: 'SignedOut',
	NetworkError: 'NetworkError',
});

export default class Communication {
	static networkErrorCode = -999999;

	static accountApiUrlPrefix = '/accountapi/v1';

	static accountApiV2UrlPrefix = '/accountapi/v2';

	static resetTimeout;

	static triggerTimeout;

	static init({ resetTimeout, triggerTimeout }) {
		this.resetTimeout = resetTimeout;
		this.triggerTimeout = triggerTimeout;
	}

	/*
	 * Iterative function to sanitize input parameters
	 */
	static sanitizeInput(input) {
		if (isEmpty(input)) {
			return input;
		}

		if (typeof input === 'string') {
			return dompurify.sanitize(input);
		} else if (Array.isArray(input)) {
			return input.map(item => this.sanitizeInput(item));
		} else if (typeof input === 'object') {
			Object.keys(input).forEach(key => {
				input[key] = this.sanitizeInput(input[key]);
			});
		}
		return input;
	}

	/**
	 * Make a HTTP request. Some request config:
	 *	@param {string} method The request method (default to 'get' when `null`).
	 * 	@param {string} url The request server URL.
	 * 	@param {object|string} data The request body.
	 *  @param {number} timeout When `null`, default to 30 seconds.
	 *
	 * For other available config options for making requests, See:
	 * https://github.com/axios/axios#request-config
	 */
	static async axiosRequest({ method = 'get', url, data, timeout = 30000, headers }) {
		if (isFunction(this.resetTimeout)) {
			this.resetTimeout();
		}

		const sanitizedData = data ? this.sanitizeInput(data) : data;

		try {
			const response = await axios({
				url,
				method,
				data: sanitizedData, // Correctly pass the sanitized data
				timeout,
				headers,
			});

			return this.processResponse(response);
		} catch (error) {
			return this.resolveError(error, url);
		}
	}

	static async axiosRequestWithBigInt({ method = 'get', url, data, timeout = 30000, headers }) {
		if (isFunction(this.resetTimeout)) {
			this.resetTimeout();
		}

		try {
			const response = await axios({
				url,
				method,
				data,
				timeout,
				headers,
				transformResponse: [rdata => rdata],
			});

			response.data = response.data ? JSONParse(response.data) : response.data;

			return this.processResponse(response);
		} catch (error) {
			return this.resolveError(error, url);
		}
	}

	static processResponse(response) {
		const { data, status } = response;

		switch (status) {
			// status: 200, 201
			case HttpStatusCode.OK:
			case HttpStatusCode.Created:
			case HttpStatusCode.Accepted:
			case HttpStatusCode.NoContent:
				/**
				 * This code block is to handle ajax calls where none-zero ErrorCode
				 * is returned even when the call is successful.
				 */
				if (isObject(data) && isNumber(data.ErrorCode) && data.ErrorCode !== 0) {
					return {
						status: CommunicationStatus.Failure,
						ErrorCode: data.ErrorCode,
						ErrorDescription: data.ErrorDescription,
					};
				}

				return {
					status: CommunicationStatus.Success,
					httpStatus: status,
					data,
				};
			// status: 400, 500
			case HttpStatusCode.BadRequest:
			case HttpStatusCode.InternalServerError:
				if (isObject(data?.Error)) {
					return this.parseSERAErrorResponse(CommunicationStatus.Failure, data);
				} else {
					let { ErrorDescription } = data;
					ErrorDescription = this.processErrorDescription(ErrorDescription);

					return {
						status: CommunicationStatus.Failure,
						ErrorCode: data.ErrorCode,
						ErrorDescription,
					};
				}
			// status: 401
			case HttpStatusCode.Unauthorized:
				// automatically sign out the ui
				if (isFunction(this.triggerTimeout)) {
					this.triggerTimeout();
				}

				// notify the caller with the status
				return {
					status: CommunicationStatus.SignedOut,
				};
			default:
				return {
					status: CommunicationStatus.NetworkError,
					ErrorCode: this.networkErrorCode,
					ErrorDescription: data,
				};
		}
	}

	/**
	 * Backend now passes SERA error response object to Front-End under
	 * 'Error' field. The same object is also returned to us under
	 * 'ErrorDescription' field, except it's stringified.
	 *
	 * Backend possible returns:
	 *
	 * Error: {
	 *     "error_reference_id": "19f4fcfa-bcd9-4409-941e-cf43c5f423d0",
	 *     "errors": [{...}]
	 * }
	 *
	 * or,
	 *
	 * Error: {
	 *     "Error": {"Code": "exception", "Message": "User cannot be activated because they have been activated already."}
	 * }
	 *
	 * For more details, see Genaro's comment in
	 * https://stamps.atlassian.net/browse/WEBAPPS-34160
	 *
	 * @param {CommunicationStatus} status Communication status.
	 * @param {object} error SERA error object.
	 * @returns {object}
	 */
	static parseSERAErrorResponse(status, error) {
		const { ErrorCode, ErrorDescription, Error } = error;

		if (isArray(Error?.errors)) {
			const { error_code: errorCode, error_message: errorMessage } = Error.errors[0];

			return {
				status,
				ErrorCode,
				ErrorDescription,
				error: {
					errorCode:
						isString(errorCode) && errorCode !== '' ? Number(errorCode) : ErrorCode,
					errorMessage,
				},
			};
		} else if (isObject(Error?.Error)) {
			return {
				status,
				ErrorCode,
				ErrorDescription: Error.Error.Message,
			};
		}

		return {
			status,
			ErrorCode,
			ErrorDescription,
		};
	}

	static resolveError(error, url) {
		const errorObj = isObject(error) ? error : {};
		const axiosError = isFunction(errorObj.toJSON) ? errorObj.toJSON() : null;

		// the server responded with a status code that falls out of the range of 2xx
		if (isObject(errorObj.response)) {
			return this.processResponse(errorObj.response);
		}
		// no response was received
		else {
			return {
				status: CommunicationStatus.NetworkError,
				ErrorCode: this.networkErrorCode,
				ErrorDescription: axiosError ? axiosError.message : error,
				url,
			};
		}
	}

	static processErrorDescription(errorDescription = '') {
		const pattern = /([0-9]+([A-Za-z][0-9]+)+)/;
		let processedErrorDesc = '';
		let errorStrArray;

		errorDescription = errorDescription.replaceAll('\n', '. ');
		errorDescription = errorDescription.replaceAll('_', ' ');

		errorStrArray = errorDescription.split('.');

		for (let index = 0; index < errorStrArray.length; index++) {
			const errorString = errorStrArray[index];

			if (!pattern.exec(errorString)) {
				processedErrorDesc += errorString;

				if (processedErrorDesc.charAt(processedErrorDesc.length - 1) !== '.') {
					processedErrorDesc += '.';
				}
			}
		}

		if (
			processedErrorDesc.search(
				'Only mail classes Priority Mail and Express Mail are allowed for this account'
			) !== -1
		) {
			processedErrorDesc =
				'Only mail classes Priority Mail and Express Mail are allowed for this account. First Class International Flat Document is not enabled for your account.';
		}

		if (processedErrorDesc.search('Missing/Invalid ShipTo PostalCode') !== -1) {
			processedErrorDesc = 'Missing/Invalid ShipTo PostalCode. Postal Code is required.';
		}

		return processedErrorDesc.trim();
	}

	static isSuccessful(data) {
		return isObject(data) && data.status === CommunicationStatus.Success;
	}

	static getUrl({ urlType, applicationContext, app, route }) {
		const queryArr = [];

		if (applicationContext) {
			queryArr.push(`ctx=${applicationContext}`);
		}
		if (app) {
			queryArr.push(`app=${app}`);
		}
		if (route) {
			queryArr.push(`route=${route}`);
		}

		const queryString = queryArr.length > 0 ? `?${queryArr.join('&')}` : '';

		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/misc/geturl/${urlType}${queryString}`,
		});
	}

	static getAccountInfo() {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/r/swsim/v1/account`,
		});
	}

	/**
	 * Return auto purchase settings
	 * @param {array}
	 */
	static getAutoPurchaseSettings() {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/r/swsim/v1/balance/auto-purchase-settings`,
		});
	}

	/**
	 * Save auto purchase settings
	 * @param {object} data
	 */
	static setAutoPurchaseSettings(data) {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/r/swsim/v1/balance/auto-purchase-settings`,
			method: 'put',
			data,
		});
	}

	static getBalance() {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/r/swsim/v1/balance`,
		});
	}

	static postLogs(data, type) {
		const queryString = type ? `?type=${type}` : '';

		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/misc/log${queryString}`,
			data,
		});
	}

	static async sendEmail(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/misc/sendemail`,
			data,
		});
	}

	static containerLabelCount() {
		const dateRangeValue = DateRange.All;
		const { dateRangeStart } = Util.calculateDateRange(dateRangeValue);

		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/r/reports/v1/prints`,
			data: {
				eligible_for_container_label: true,
				transaction_date_start: dateRangeStart,
				max_results: 100,
			},
		});
	}

	static refundAssistLabelCount() {
		const dateRangeValue = DateRange.All;
		const { dateRangeStart } = Util.calculateDateRange(dateRangeValue);

		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/r/reports/v1/prints`,
			data: {
				eligible_for_refund_assist: true,
				transaction_date_start: dateRangeStart,
				max_results: 100,
			},
		});
	}

	static getSession({ winClientVersion, InternalAccountInfo }) {
		const cver = `cver=${winClientVersion || null}`;
		const actinfo = `actinfo=${InternalAccountInfo === true}`;

		return this.axiosRequest({
			url: `${this.accountApiV2UrlPrefix}/auth/session?${cver}&${actinfo}`,
		});
	}

	static startAccountVerification(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/account/startVerification`,
			data,
		});
	}

	static finishAccountVerification(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/account/finishVerification`,
			data,
		});
	}

	static getUsers() {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/user`,
			timeout: 180000,
		});
	}

	static authenticateUser(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/auth`,
			data,
		});
	}

	static finishPasswordReset(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/auth/finishPasswordReset`,
			data,
		});
	}

	static completeAuth(data) {
		const queryParams = `code=${data.code}&state=${data.state}`;
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/auth/completeAuth?${queryParams}`,
		});
	}

	static setCodewords(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/auth/codewords`,
			data,
		});
	}

	static recoverUsername(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/auth/recoverUserName`,
			data,
		});
	}

	static getCodewordQuestions({ Username }) {
		const encodedUsername = encodeURIComponent(Username);

		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/auth/codewordquestions?username=${encodedUsername}`,
		});
	}

	static startPasswordReset(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/auth/startPasswordReset`,
			data,
		});
	}

	static async parseAddress(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/r/avs/v1/addresses/parse`,
			data,
		});
	}

	static async validateAddress(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/r/avs/v1/addresses/validate`,
			data,
			headers: {
				'X-Extended-Info': true,
			},
		});
	}

	static signOut() {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/auth/signout`,
		});
	}

	static addCarrier(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/carrier`,
			data,
		});
	}

	static initiateMonoauth(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/monoauth/url`,
			data,
		});
	}

	static monoauthAddCarrier(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/monoauth/addcarrier`,
			data,
		});
	}

	static getCarriers() {
		return this.axiosRequest({
			method: 'get',
			url: `${this.accountApiUrlPrefix}/r/swsim/v1/carriers`,
		});
	}

	static addCarrierToLocation(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/carrier/location`,
			data,
		});
	}

	static addCarrierToLocations(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/carrier/bulklocation`,
			data,
		});
	}

	static getMarketingCampaignToShow({ appType }) {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/MarketingCampaign/${appType}`,
		});
	}

	static recordMarketingCampaignActivity(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/MarketingCampaign/RecordActivity`,
			data,
		});
	}

	static getTerms(termType) {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/misc/terms/${termType}`,
		});
	}

	static acceptTerms(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/user/terms`,
			data,
		});
	}

	static getCostCodes(custId = 0) {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/costcode/${custId}`,
		});
	}

	static addCostCodes(data, custId = 0) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/costcode/${custId}`,
			data,
		});
	}

	static deleteCostCodes(data, custId = 0) {
		return this.axiosRequest({
			method: 'delete',
			url: `${this.accountApiUrlPrefix}/costcode/${custId} `,
			data,
		});
	}

	/**
	 * Return the services' rates. To get USPS rates, pass 'usps' in the 2nd arguement; and
	 * to get all rates of all carriers, don't pass the second arguement.
	 * @param {object} data API payload.
	 * @param {Carrier} carriers Pass `null` to get all rates of all carriers.
	 * @param {array}
	 */
	static getRates(data, carriers) {
		const queryParam = carriers ? `?carriers=${carriers}` : '';

		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/r/swsim/v1/rates/${queryParam}`,
			data,
		});
	}

	static getShipFromContacts() {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/contacts/shipfromcontact`,
		});
	}

	static getCarrierServices(shipDate) {
		const queryParam = shipDate ? `?ship_date=${shipDate}` : '';

		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/r/swsim/v1/carrier-services/${queryParam}`,
		});
	}

	static setPreferences(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/account/preferences`,
			data,
		});
	}

	static getCustomerContactInfo(custId = 0) {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/account/CustomerContactInfo/${custId}`,
		});
	}

	static updateCustomerContactInfo(data) {
		return this.axiosRequest({
			method: 'put',
			url: `${this.accountApiUrlPrefix}/account/CustomerContactInfo`,
			data,
		});
	}

	static deleteImage(imageId = 0) {
		return this.axiosRequest({
			method: 'delete',
			url: `${this.accountApiUrlPrefix}/account/image/${imageId}`,
		});
	}

	static uploadImage(data, imageCategory, headers = null) {
		const pathName = imageCategory ? `/${imageCategory}` : '';

		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/account/image${pathName}`,
			data,
			headers,
		});
	}

	static getBrandedTracking() {
		return this.axiosRequest({
			method: 'get',
			url: `${this.accountApiUrlPrefix}/brandedtracking/settings`,
		});
	}

	static modifyBrandedTracking(data) {
		return this.axiosRequest({
			method: 'put',
			url: `${this.accountApiUrlPrefix}/brandedtracking/settings`,
			data,
		});
	}

	static createBrandedTracking(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/brandedtracking/settings`,
			data,
		});
	}

	static deleteBrandedTracking(brandingId) {
		return this.axiosRequest({
			method: 'delete',
			url: `${this.accountApiUrlPrefix}/brandedtracking/settings/${brandingId}`,
		});
	}

	static getNotificationSetting() {
		return this.axiosRequest({
			method: 'get',
			url: `${this.accountApiUrlPrefix}/brandedtracking/notification`,
		});
	}

	static modifyNotificationSetting(data) {
		return this.axiosRequest({
			method: 'put',
			url: `${this.accountApiUrlPrefix}/brandedtracking/notification`,
			data,
		});
	}

	static createNotificationSetting(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/brandedtracking/notification`,
			data,
		});
	}

	static deleteNotificationSetting(notificationSettingId) {
		return this.axiosRequest({
			method: 'delete',
			url: `${this.accountApiUrlPrefix}/brandedtracking/notification/${notificationSettingId}`,
		});
	}

	/**
	 * Add a new Tag
	 * @param {object} data tag data
	 */
	static async addTag(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/ssapi/tags`,
			data,
		});
	}

	/**
	 * Delete Tag
	 * @param {number} tagId
	 */
	static async deleteTag(tagId) {
		return this.axiosRequest({
			method: 'delete',
			url: `${this.accountApiUrlPrefix}/ssapi/tags/${tagId}`,
			data: {},
		});
	}

	/**
	 * Update Tag
	 * @param {number} tagId
	 * @param {object} data tag data
	 */
	static async updateTag(data) {
		return this.axiosRequest({
			method: 'put',
			url: `${this.accountApiUrlPrefix}/ssapi/tags/${data.OrderTagID}`,
			data,
		});
	}

	/**
	 * Get Product Tags
	 */
	static async getTags() {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/ssapi/tags`,
		});
	}

	static getCustStorage() {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/account/custstorage`,
		});
	}

	static setCustStorage(data) {
		return this.axiosRequest({
			method: 'put',
			url: `${this.accountApiUrlPrefix}/account/custstorage`,
			data,
		});
	}

	static resubmitPurchase(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/payment/ResubmitPurchase`,
			data,
		});
	}

	static purchasePostage(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/r/swsim/v1/balance/add-funds`,
			data,
		});
	}

	static createLabels(data) {
		return this.axiosRequest({
			method: 'POST',
			url: `${this.accountApiUrlPrefix}/r/swsim/v1/labels`,
			data,
		});
	}

	static createMediaGen(data) {
		return this.axiosRequest({
			method: 'POST',
			url: `${this.accountApiUrlPrefix}/mediagen`,
			data,
		});
	}

	static createPackingSlip(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/print/packingslip`,
			data,
		});
	}

	static getShipNotificationEmail() {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/account/shipnotification`,
		});
	}

	static setShipNotificationEmail(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/account/shipnotification`,
			data,
		});
	}

	static testShipNotificationEmail(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/account/testshipnotificationemail`,
			data,
		});
	}

	static createInbound(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/r/zorbit/v1/receivings`,
			data,
		});
	}

	static updateInbound(receivingId, data) {
		return this.axiosRequest({
			method: 'put',
			url: `${this.accountApiUrlPrefix}/r/zorbit/v1/receivings/${receivingId}`,
			data,
		});
	}

	static async getZorbitWarehouses() {
		return this.axiosRequest({
			method: 'get',
			url: `${this.accountApiUrlPrefix}/r/zorbit/v1/warehouses`,
		});
	}

	static async getProducts() {
		// TODO: Replace the return with axios request once Zorbit API URL is available
		/* return this.axiosRequest({
			method: 'get',
			url: `${this.inboundsApiUrlPrefix}/products/`,
		}); */

		return {
			status: 'Success',
			data: {
				ErrorCode: 0,
				products: [
					{
						product_id: 'fbf9eb6c-0499-4729-9c52-7201b6472d89',
						sku: 'A77015900-7424-7424-7424-496ae693ee7b',
						product_barcode: '123455654645',
						barcode_type: 'UPC',
						name: 'Test241',
						product_type: 'package',
						image_url: 'https://test.com',
						is_active: true,
						alternative_skus: [
							{
								sku: 'sku',
							},
						],
						measurements: {
							weight: 10,
							weight_unit: 'lbs',
							length: 2,
							width: 3,
							height: 4,
							dimensions_unit: 'lbs',
						},
						customs_info: {
							harmonized_tariff_code: 'Tariff code',
							unit_value: {
								amount: 20,
								currency: '$',
							},
							description: 'Customs Info Description',
						},
						kit_content: [
							{
								product_id: 'Product kit',
								quantity: 5,
							},
						],
						attributes: {
							xxx: 'xxx',
						},
					},
					{
						product_id: '6b1e8592-63a5-4f15-88da-ac58a58cd397',
						sku: 'A29303761-8418-8418-8418-496ae693ee7b',
						product_barcode: '',
						barcode_type: null,
						name: 'Test880',
						product_type: 'package',
						image_url: 'https://test.com',
						is_active: true,
						alternative_skus: [
							{
								sku: 'sku',
							},
						],
						measurements: {
							weight: 10,
							weight_unit: 'lbs',
							length: 2,
							width: 3,
							height: 4,
							dimensions_unit: 'lbs',
						},
						customs_info: {
							harmonized_tariff_code: 'Tariff code',
							unit_value: {
								amount: 20,
								currency: '$',
							},
							description: 'Customs Info Description',
						},
						kit_content: [
							{
								product_id: 'Product kit',
								quantity: 5,
							},
						],
						attributes: {
							xxx: 'xxx',
						},
					},
				],
			},
		};
	}

	static getImageList() {
		return this.axiosRequest({
			method: 'get',
			url: `${this.accountApiUrlPrefix}/account/image`,
		});
	}

	static createIndicium(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/print/indicium`,
			data,
			headers: {
				'Content-Type': 'application/json; charset=utf-8',
			},
		});
	}

	static createEnvelopeIndicium(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/print/envelope`,
			data,
			headers: {
				'Content-Type': 'application/json; charset=utf-8',
			},
		});
	}

	static billingPlans() {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/CloseAccount/BillingPlans`,
		});
	}

	static async changePlan(data) {
		let { PlanId: newPlanId, newPlanPrice = 0 } = data;
		const accountInfo = Session.getAccountInfo();
		const { planId: oldPlanId, currentPlanServiceFee: oldPlanPrice } = accountInfo.planDetails;

		if (data.hasOwnProperty('newPlanPrice')) {
			delete data.newPlanPrice;
		}

		const response = await this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/CloseAccount/ChangePlan`,
			data,
		});

		if (this.isSuccessful(response)) {
			if (newPlanId === 278) {
				newPlanPrice = 15.99;
			}
			Analytics.trackEvent(
				'changePlan',
				{
					newPlanId,
					newPlanPrice,
					oldPlanId,
					oldPlanPrice,
				},
				Analytics.agent.segment
			);
		}

		return response;
	}

	/**
	 * @param {object} data
	 * @returns {object}
	 */
	static getReprintCommand(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/print/command`,
			data,
		});
	}

	/**
	 * @param {object} data
	 * @returns {object}
	 */
	static updateAccountCapabilities(capability) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/account/updateaccountcapabilities/${capability}`,
		});
	}
}
